// @dynamic
export class SOVerify {
    // you probably want to use isDefinedOrNull()
    private static isDefined(input: any): boolean {
        return input !== void 0;
    }

    public static isNull(input: any): boolean {
        return input === null;
    }

    public static isUndefined(input: any): boolean {
        return !SOVerify.isDefined(input);
    }

    public static isNotNull(input: any): boolean {
        return !SOVerify.isNull(input);
    }

    public static isUndefinedOrNull(input: any): boolean {
        return SOVerify.isUndefined(input) || SOVerify.isNull(input);
    }

    public static isDefinedOrNull(input: any): boolean {
        return SOVerify.isDefined(input) || SOVerify.isNull(input);
    }

    public static isDefinedAndNotNull = (...inputs: any[]): boolean => {
        let returnValue: boolean = true;
        inputs.forEach((input) => {
            if ((SOVerify.isDefined(input) && SOVerify.isNotNull(input)) === false) {
                returnValue = false;
                return; // out of forEach
            }
        });
        return returnValue;
    };

    public static getValueFromStructure<T>(
        objectOrArray: Object | any[],
        ...nestedAttributes: Array<string | number>
    ): T {
        let returnValue: T = null;
        if (SOVerify.isDefinedAndNotNull(objectOrArray, nestedAttributes) && this.arrayHasElements(nestedAttributes)) {
            let tmp: any = objectOrArray;
            for (let i: number = 0; i < nestedAttributes.length; ++i) {
                if (SOVerify.isNull(tmp) || SOVerify.isDefined(tmp[nestedAttributes[i]]) === false) {
                    tmp = null;
                }
                tmp = this.isDefinedAndNotNullStructure(tmp, nestedAttributes[i]) ? tmp[nestedAttributes[i]] : null;
            }
            returnValue = tmp;
        }
        return returnValue;
    }

    /**
   * check if the objects and its substructure is defined and not null
   * how to use:
   * // is this.universe.milkyway.solarsystem.earth defined?
   *  if(SOVerify.isDefinedStructure(this.universe, 'milkyway', 'solarsystem', 'earth') {
         // create humans
        }
   * // you can verify arrays by using the index number
   *  if(SOVerify.isDefinedStructure(myArray, 10, 'name') {
          doSomething(myArray[10].name)
        }
   */
    public static isDefinedStructure(
        objectOrArray: object | any[],
        ...nestedAttributes: Array<string | number>
    ): boolean {
        if (SOVerify.isUndefined(objectOrArray)) {
            return false;
        }
        let tmp: any = objectOrArray;
        for (let i: number = 0; i < nestedAttributes.length; ++i) {
            if (SOVerify.isNull(tmp) || SOVerify.isDefined(tmp[nestedAttributes[i]]) === false) {
                return false;
            }
            tmp = tmp[nestedAttributes[i]];
        }
        return true;
    }

    public static isDefinedAndNotNullStructure(
        objectOrArray: Object | any[],
        ...nestedAttributes: Array<string | number>
    ): boolean {
        if (SOVerify.isUndefinedOrNull(objectOrArray)) {
            return false;
        }
        let tmp: any = objectOrArray;
        for (let i: number = 0; i < nestedAttributes.length; ++i) {
            if (SOVerify.isUndefinedOrNull(tmp[nestedAttributes[i]])) {
                return false;
            }
            tmp = tmp[nestedAttributes[i]];
        }
        return true;
    }

    public static setValueFromStructure<T>(
        objectOrArray: object | any[],
        newValue: T,
        ...nestedAttributes: Array<string | number>
    ): void {
        if (SOVerify.isDefinedAndNotNull(objectOrArray, nestedAttributes) && this.arrayHasElements(nestedAttributes)) {
            let tmp: any = objectOrArray;
            for (let i: number = 0; i < nestedAttributes.length - 1; ++i) {
                if (SOVerify.isNull(tmp) || SOVerify.isDefined(tmp[nestedAttributes[i]]) === false) {
                    tmp = null;
                } else {
                    tmp = tmp[nestedAttributes[i]];
                }
            }
            tmp[nestedAttributes[nestedAttributes.length - 1]] = <T>newValue;
        }
    }

    public static isBoolean(input: any): boolean {
        return typeof input === 'boolean';
    }

    public static isString(input: any): boolean {
        return typeof input === 'string';
    }

    public static isNumber(input: any): boolean {
        return typeof input === 'number' && isNaN(input) === false;
    }

    public static isObject(input: any): boolean {
        return typeof input === 'object';
    }

    public static isObjectWithFilledProperties(input: any): boolean {
        let returnValue: boolean =
            typeof input === 'object' && SOVerify.isDefinedAndNotNull(input) && Object.keys(input).length > 0;

        if (returnValue) {
            Object.keys(input).forEach((key: string) => {
                returnValue = returnValue && SOVerify.isDefinedAndNotNullStructure(input, key);
            });
        }
        return returnValue;
    }

    public static isObjectWithProperties(input: any): boolean {
        return typeof input === 'object' && Object.keys(input).length > 0;
    }

    public static isEmptyObject(input: any): boolean {
        return typeof input === 'object' && Object.keys(input).length === 0;
    }

    public static isFunction(input: any): boolean {
        return typeof input === 'function';
    }

    public static isValidDate(input: any): boolean {
        return input instanceof Date && !isNaN(input.valueOf());
    }

    public static arrayHasElements(input: any): boolean {
        return Array.isArray(input) && input.length > 0;
    }
}

/**
 * Picks a (nested) value out of any structure
 * @param accessFn: e.g. getValue(() => this.product.data.favorite)
 * @param fallbackValue: will be returned if the accessed value is undefined or null
 */
export const getValue = <T>(accessFn: () => T, fallbackValue: any = undefined): T | any => {
    try {
        const value = accessFn();
        return value !== void 0 && value !== null ? value : fallbackValue;
    } catch (e) {
        return fallbackValue;
    }
};

/**
 * True if the given value is not null and not undefined
 * @param accessFn: e.g. isDefined(() => this.product.data.favorite)
 */
export const isUndefinedOrNull = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value === void 0 || value === null;
};

/**
 * True if the given value is not undefined but can be null
 * @param accessFn: e.g. isDefined(() => this.product.data.favorite)
 */
export const isDefinedAllowNull = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value !== void 0;
};

/**
 * True if the given value is not null and not undefined
 * @param accessFn: e.g. isDefined(() => this.product.data.favorite)
 */
export const isDefined = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value !== void 0 && value !== null;
};

/**
 * True if the given value is null or undefined
 * @param accessFn: e.g. isUndefined(() => this.product.data.favorite)
 */
export const isUndefined = (accessFn: () => any) => !isDefined(accessFn);

export const isDefinedAndFilledArray = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value !== void 0 && value !== null && SOVerify.arrayHasElements(value);
};

export const isDefinedAndFilledString = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value !== void 0 && value !== null && value !== '';
};

/**
 * True if the given value is not null, not undefined and not NaN
 * @param accessFn: e.g. isDefinedAndNotNaN(() => this.product.data.favorite)
 */
export const isDefinedAndNotNaN = (accessFn: () => any): boolean => {
    const value = getValue(accessFn);
    return value !== void 0 && value !== null && !isNaN(value);
};
